import { useEffect, useRef } from 'react';
import { Animated, Platform, StyleSheet, Text, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

/**
 * InlineNotification - Reusable notification component
 *
 * @param {boolean} visible - Whether to show the notification
 * @param {string} type - Type of notification: 'success' | 'error' | 'warning' | 'info'
 * @param {string} message - Notification message text
 * @param {Function} onDismiss - Called when notification auto-dismisses
 * @param {number} duration - Auto-dismiss duration in ms (default: 8000)
 */
export const InlineNotification = ({
  visible = false,
  type = 'info',
  message = '',
  onDismiss = () => {},
  duration = 8000,
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Fade in
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();

      // Auto-dismiss after duration
      const timer = setTimeout(() => {
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          onDismiss();
        });
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [visible, fadeAnim, onDismiss, duration]);

  if (!visible) return null;

  const config = getNotificationConfig(type);

  return (
    <Animated.View
      style={[
        styles.notification,
        {
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
          opacity: fadeAnim,
        },
      ]}
    >
      <MaterialCommunityIcons name={config.icon} size={20} color={config.iconColor} style={styles.icon} />
      <Text style={[styles.text, { color: config.textColor }]}>{message}</Text>
    </Animated.View>
  );
};

/**
 * Get notification config based on type
 */
const getNotificationConfig = (type) => {
  switch (type) {
    case 'success':
      return {
        icon: 'check-circle',
        iconColor: '#16A34A',
        backgroundColor: '#F0FDF4',
        borderColor: '#16A34A',
        textColor: '#15803D',
      };
    case 'error':
      return {
        icon: 'alert-circle',
        iconColor: '#DC2626',
        backgroundColor: '#FEF2F2',
        borderColor: '#DC2626',
        textColor: '#991B1B',
      };
    case 'warning':
      return {
        icon: 'alert',
        iconColor: '#F59E0B',
        backgroundColor: '#FFFBEB',
        borderColor: '#F59E0B',
        textColor: '#92400E',
      };
    case 'info':
    default:
      return {
        icon: 'information',
        iconColor: '#144CB8',
        backgroundColor: '#F0F4FF',
        borderColor: '#144CB8',
        textColor: '#1E40AF',
      };
  }
};

const styles = StyleSheet.create({
  notification: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginTop: 16,
    marginHorizontal: 16,
    gap: 12,
    borderLeftWidth: 4,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  icon: {
    marginTop: 2,
  },
  text: {
    flex: 1,
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 20,
  },
});

export default InlineNotification;
